import React, { useState, useEffect } from 'react';
import { useUsers } from './UsersContext';
import { T, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const ApproveArticlesSection = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [rejectModal, setRejectModal] = useState({ show: false, contentId: null });
  const [rejectComment, setRejectComment] = useState('');
  const [viewArticle, setViewArticle] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  const [search, setSearch] = useState('');
  const reviewerName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || localStorage.getItem('vrvEmail') || '';

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-articlecontent-getAll`);
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setArticles((parsed.data || []).filter(a => a.status === 'PENDING').sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPending(); }, [refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleAction = async (content_id, action, comments = '') => {
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-articlecontent-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content_id, action, reviewedBy: reviewerName, reviewedAt: new Date().toISOString(), reviewComments: action === 'APPROVE' ? 'APPROVED' : comments }),
      });
      if (res.ok) { showMsg(`Article ${action.toLowerCase()}d!`, 'success'); fetchPending(); triggerRefresh(); }
      else showMsg('Action failed', 'error');
    } catch { showMsg('Error connecting', 'error'); }
  };

  const filtered = articles.filter(a => !search || Object.values(a).some(v => String(v).toLowerCase().includes(search.toLowerCase())));

  if (loading) return <div className="text-center py-16"><p className="text-sm" style={{ color: T.muted }}>Loading…</p></div>;
  if (!articles.length) return (
    <div className="vrv-card text-center py-16">
      <div className="text-4xl mb-3">✅</div>
      <p className="font-semibold" style={{ color: T.muted }}>No pending articles to approve.</p>
    </div>
  );

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

      {/* Desktop Table */}
      <div className="desktop-table">
        <TableCard>
          <TableBar>
            <p className="text-xs font-bold" style={{ color: T.gold }}>⏳ {filtered.length} article(s) pending approval</p>
          </TableBar>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Title</Th><Th>Created By</Th><Th>Date</Th><Th center>Actions</Th></tr></thead>
              <tbody>
                {filtered.map((a, i) => (
                  <Tr key={a.content_id || i}>
                    <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{a.title || '—'}</td>
                    <td className="px-3 py-2.5">
                      {a.createdBy
                        ? <button onClick={() => { const u = getUserObj(a.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline text-xs" style={{ color: T.gold }}>{getUserName(a.createdBy) || '...'}</button>
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex gap-1.5 justify-center">
                        <ActionBtn onClick={() => setViewArticle(a)} color="muted" small>View</ActionBtn>
                        <ActionBtn onClick={() => handleAction(a.content_id, 'APPROVE')} color="green" small>Approve</ActionBtn>
                        <ActionBtn onClick={() => setRejectModal({ show: true, contentId: a.content_id })} color="red" small>Reject</ActionBtn>
                      </div>
                    </td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCard>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        <p className="text-xs font-bold mb-3" style={{ color: T.gold }}>⏳ {filtered.length} article(s) pending approval</p>
        {filtered.map((a, i) => (
          <div key={a.content_id || i} className="vrv-mobile-card">
            <p className="font-bold text-sm truncate mb-1" style={{ color: T.navy }}>{a.title || '—'}</p>
            <div className="grid grid-cols-2 gap-1 text-xs mb-3">
              <div><span style={{ color: T.muted }}>By: </span>{a.createdBy ? <button onClick={() => { const u = getUserObj(a.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(a.createdBy) || '...'}</button> : '—'}</div>
              <div><span style={{ color: T.muted }}>Date: </span>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</div>
            </div>
            <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={() => setViewArticle(a)} color="muted" small>View</ActionBtn>
              <ActionBtn onClick={() => handleAction(a.content_id, 'APPROVE')} color="green" small>Approve</ActionBtn>
              <ActionBtn onClick={() => setRejectModal({ show: true, contentId: a.content_id })} color="red" small>Reject</ActionBtn>
            </div>
          </div>
        ))}
      </div>

      {viewArticle && (
        <Modal onClose={() => setViewArticle(null)} title={viewArticle.title} subtitle="Article Preview" wide>
          <p className="text-xs mb-4" style={{ color: T.muted }}>By {viewArticle.createdBy || 'Unknown'} · {viewArticle.createdAt ? new Date(viewArticle.createdAt).toLocaleDateString() : ''}</p>
          <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6 pt-4" style={{ color: T.ink, borderTop: '1px solid rgba(0,0,0,0.06)' }}>{viewArticle.content}</p>
          <div className="flex gap-3">
            <PrimaryBtn onClick={() => setViewArticle(null)}>Close</PrimaryBtn>
            <ActionBtn onClick={() => { handleAction(viewArticle.content_id, 'APPROVE'); setViewArticle(null); }} color="green">Approve</ActionBtn>
            <ActionBtn onClick={() => { setViewArticle(null); setRejectModal({ show: true, contentId: viewArticle.content_id }); }} color="red">Reject</ActionBtn>
          </div>
        </Modal>
      )}

      {viewUser && (
        <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide>
          <UserDetailFields user={viewUser} />
          <div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}

      {rejectModal.show && (
        <RejectModal
          value={rejectComment}
          onChange={e => setRejectComment(e.target.value)}
          title="Reject Article"
          onCancel={() => { setRejectModal({ show: false, contentId: null }); setRejectComment(''); }}
          onConfirm={() => { handleAction(rejectModal.contentId, 'REJECT', rejectComment); setRejectModal({ show: false, contentId: null }); setRejectComment(''); }}
        />
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default ApproveArticlesSection;
